import React, { useState, useEffect, useMemo } from "react";
import { getData, postData, putData } from "../../services/api.service";
import { toastSuccess, toastError } from "../common/Toast";
import {
  FaLink,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaExclamationTriangle,
  FaFilter
} from "react-icons/fa";

function SkuMaterialRefsManager() {
  const [references, setReferences] = useState([]);
  const [skus, setSkus] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingRef, setEditingRef] = useState(null);
  const [deletingRef, setDeletingRef] = useState(null);
  const [filterSkuId, setFilterSkuId] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [formData, setFormData] = useState({
    sku_id: '',
    material_id: '',
    active: true
  });

  useEffect(() => {
    loadReferences();
    loadSkus();
    loadMaterials();
  }, []);

  useEffect(() => {
    if (filterSkuId) {
      loadReferences(filterSkuId);
    } else {
      loadReferences();
    }
  }, [filterSkuId]);

  const loadReferences = async (skuId = null) => {
    try {
      setLoading(true);
      
      const url = skuId 
        ? `/api/admin/get_sku_material_refs?sku_id=${skuId}`
        : '/api/admin/get_sku_material_refs';
      
      const response = await getData(url);
      
      if (response && response.data) {
        // La respuesta puede venir de diferentes formas según la API
        let refsData = [];
        if (Array.isArray(response.data)) {
          refsData = response.data;
        } else if (response.data.references && Array.isArray(response.data.references)) {
          refsData = response.data.references;
        } else if (response.data.skuMaterialRefs && Array.isArray(response.data.skuMaterialRefs)) {
          refsData = response.data.skuMaterialRefs;
        }
        
        setReferences(refsData);
      } else {
        setReferences([]);
      }
      
    } catch (error) {
      console.error('Error cargando referencias:', error);
      toastError('Error al cargar las referencias SKU-Material');
      setReferences([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSkus = async () => {
    try {
      const response = await getData('/api/admin/get_sku_mains');
      
      let skusData = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          skusData = response.data;
        } else if (response.data.skus && Array.isArray(response.data.skus)) {
          skusData = response.data.skus;
        } else if (response.data.skuMains && Array.isArray(response.data.skuMains)) {
          skusData = response.data.skuMains;
        } else {
          // Buscar cualquier array en la respuesta
          Object.keys(response.data).forEach(key => {
            if (Array.isArray(response.data[key]) && skusData.length === 0) {
              skusData = response.data[key];
            }
          });
        }
      }
      
      // Filtrar solo SKUs activos
      const activeSkus = skusData.filter(sku => sku.active);
      setSkus(activeSkus);
      
    } catch (error) {
      console.error('Error cargando SKUs:', error);
      toastError('Error al cargar los SKUs');
      setSkus([]);
    }
  };

  const loadMaterials = async () => {
    try {
      const response = await getData('/api/admin/get_materials');
      
      let materialsData = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          materialsData = response.data;
        } else if (response.data.materials && Array.isArray(response.data.materials)) {
          materialsData = response.data.materials;
        }
      }
      
      // Filtrar solo materiales activos
      const activeMaterials = materialsData.filter(material => material.active);
      setMaterials(activeMaterials);
      
    } catch (error) {
      console.error('Error cargando materiales:', error);
      toastError('Error al cargar los materiales');
      setMaterials([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const dataToSend = {
        data: {
          sku_id: parseInt(formData.sku_id),
          material_id: parseInt(formData.material_id),
          active: formData.active === true ? true : formData.active, // Mantener como boolean si es true
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };

      if (editingRef) {
        const response = await putData('/api/admin/update_sku_material_ref', dataToSend);
        
        // Verificar si la respuesta es un error
        if (response.response && response.response.status >= 400) {
          throw new Error(response.response.data?.msg || 'Error al actualizar');
        }
        
        // Actualización optimista
        setReferences(prevRefs => prevRefs.map(ref => 
          ref.sku_id === parseInt(formData.sku_id) && ref.material_id === parseInt(formData.material_id)
            ? {
                ...ref,
                sku_name: skus.find(s => s.id === parseInt(formData.sku_id))?.sku_description || ref.sku_name,
                material_name: materials.find(m => m.id === parseInt(formData.material_id))?.material_name || ref.material_name,
                active: formData.active
              }
            : ref
        ));
        
        toastSuccess('Referencia actualizada exitosamente');
      } else {
        const response = await postData('/api/admin/create_sku_material_ref', dataToSend);
        
        // Verificar si la respuesta es un error
        if (response.response && response.response.status >= 400) {
          throw new Error(response.response.data?.msg || 'Error al crear');
        }
        
        // Actualización optimista al crear
        if (response.data) {
          const newRef = {
            sku_id: parseInt(formData.sku_id),
            material_id: parseInt(formData.material_id),
            sku_name: skus.find(s => s.id === parseInt(formData.sku_id))?.sku_description || '',
            sku: skus.find(s => s.id === parseInt(formData.sku_id))?.sku || '',
            material_name: materials.find(m => m.id === parseInt(formData.material_id))?.material_name || '',
            active: formData.active
          };
          setReferences(prev => [newRef, ...prev]);
        }
        
        toastSuccess('Referencia creada exitosamente');
        loadReferences(filterSkuId || null).catch(() => {});
      }
      
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Error guardando referencia:', error);
      console.error('Error details:', error.response?.data || error.message); // Debug log
      toastError(error.message || (editingRef ? 'Error al actualizar la referencia' : 'Error al crear la referencia'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingRef(null);
    setFormData({
      sku_id: '',
      material_id: '',
      active: true
    });
  };

  const handleEdit = (ref) => {
    setEditingRef(ref);
    setFormData({
      sku_id: ref.sku_id || '',
      material_id: ref.material_id || '',
      active: ref.active !== undefined ? ref.active : true
    });
    setShowModal(true);
  };

  const handleNew = () => {
    setEditingRef(null);
    setFormData({
      sku_id: '',
      material_id: '',
      active: true
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDelete = (ref) => {
    setDeletingRef(ref);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingRef) return;
    
    try {
      setLoading(true);
      
      const dataToSend = {
        sku_id: deletingRef.sku_id,
        material_id: deletingRef.material_id
      };
      
      
      // Usar instanceAPI directamente para DELETE con body
      const { default: instanceAPI } = await import("../../api/axios.service");
      const response = await instanceAPI.delete('/api/admin/delete_sku_material_ref', {
        data: { data: dataToSend }
      });
      
      toastSuccess('Referencia eliminada exitosamente');
      setShowDeleteModal(false);
      setDeletingRef(null);
      await loadReferences(filterSkuId || null);
    } catch (error) {
      console.error('Error eliminando referencia:', error);
      console.error('Error details:', error.response?.data || error.message); // Debug log
      toastError('Error al eliminar la referencia');
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingRef(null);
  };

  const handleFilterChange = (e) => {
    setFilterSkuId(e.target.value);
  };

  const clearFilter = () => {
    setFilterSkuId('');
  };

  const getSkuDisplayName = (skuId) => {
    const sku = skus.find(s => s.id === skuId);
    if (!sku) return `SKU #${skuId}`;
    
    // Priorizar descripción, luego nombre, luego ID
    return sku.sku_description || sku.sku_name || `SKU #${skuId}`;
  };

  const getMaterialName = (materialId) => {
    const material = materials.find(m => m.id === materialId);
    return material ? material.material_name : `Material #${materialId}`;
  };

  // Filtrar referencias según el toggle - Optimizado con useMemo
  const filteredReferences = useMemo(() => {
    return showInactive ? references : references.filter(ref => ref.active);
  }, [references, showInactive]);

  return (
    <div className="sku-material-refs-manager">
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-3">
            <FaLink className="me-2 text-primary" />
            Referencias SKU-Material
          </h5>
          
          {/* Filtros */}
          <div className="row mb-3">
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-text">
                  <FaFilter />
                </span>
                <select
                  className="form-select"
                  value={filterSkuId}
                  onChange={handleFilterChange}
                  disabled={loading}
                >
                  <option value="">
                    {skus.length === 0 ? 'No hay SKUs disponibles' : 'Todos los SKUs'}
                  </option>
                  {skus.map(sku => (
                    <option key={sku.id} value={sku.id}>
                      {sku.sku_description || sku.sku_name || `SKU #${sku.id}`}
                    </option>
                  ))}
                </select>
                {filterSkuId && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={clearFilter}
                    disabled={loading}
                    title="Limpiar filtro"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            <div className="col-md-6 text-end">
              <div className="d-flex gap-2 justify-content-end align-items-center">
                <button
                  className="btn btn-primary"
                  onClick={handleNew}
                  disabled={loading}
                >
                  <FaPlus className="me-2" />
                  Nueva Referencia
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="card-body">
          {!showInactive && references.length > filteredReferences.length && (
            <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
              <div>
                <i className="fas fa-info-circle me-2"></i>
                Hay <strong>{references.length - filteredReferences.length}</strong> elemento(s) inactivo(s) oculto(s).
              </div>
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={() => setShowInactive(true)}
              >
                Mostrar todos
              </button>
            </div>
          )}

          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <div className="mt-2 text-muted">Cargando referencias...</div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Material</th>
                    <th>Estado</th>
                    <th>Fecha Creación</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReferences.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-4">
                        {filterSkuId ? (
                          showInactive ? 'No hay referencias para el SKU seleccionado' : 'No hay referencias activas para el SKU seleccionado'
                        ) : (
                          showInactive ? 'No hay referencias registradas' : 'No hay referencias activas. Activa el toggle "Mostrar inactivos" para ver referencias eliminadas.'
                        )}
                      </td>
                    </tr>
                  ) : (
                    filteredReferences.map((ref, index) => (
                      <tr key={`${ref.sku_id}-${ref.material_id}-${index}`}>
                        <td>
                          <span className="badge bg-primary">
                            {getSkuDisplayName(ref.sku_id)}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-info">
                            {getMaterialName(ref.material_id)}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${ref.active ? 'bg-success' : 'bg-danger'}`}>
                            {ref.active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td>
                          <small className="text-muted">
                            {ref.inserted_date ? new Date(ref.inserted_date).toLocaleDateString('es-AR') : 'N/A'}
                          </small>
                        </td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => handleEdit(ref)}
                            disabled={loading}
                            title="Editar referencia"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(ref)}
                            disabled={loading}
                            title="Eliminar referencia"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal para crear/editar referencia */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingRef ? 'Editar Referencia SKU-Material' : 'Nueva Referencia SKU-Material'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                ></button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">SKU *</label>
                    <select
                      className="form-select"
                      name="sku_id"
                      value={formData.sku_id}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                    >
                      <option value="">
                        {skus.length === 0 ? 'No hay SKUs disponibles' : 'Seleccionar SKU'}
                      </option>
                      {skus.map(sku => (
                        <option key={sku.id} value={sku.id}>
                          {sku.sku_description || sku.sku_name || `SKU #${sku.id}`}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      {skus.length === 0 
                        ? 'No se encontraron SKUs activos. Verifica que existan SKUs principales configurados.'
                        : 'Selecciona el SKU principal'
                      }
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Material *</label>
                    <select
                      className="form-select"
                      name="material_id"
                      value={formData.material_id}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                    >
                      <option value="">Seleccionar Material</option>
                      {materials.map(material => (
                        <option key={material.id} value={material.id}>
                          {material.material_name}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      Selecciona el material asociado
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="active"
                        checked={formData.active}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                      <label className="form-check-label">
                        Referencia activa
                      </label>
                    </div>
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !formData.sku_id || !formData.material_id}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {editingRef ? 'Actualizar' : 'Crear'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FaExclamationTriangle className="text-warning me-2" />
                  Confirmar Eliminación
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cancelDelete}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <p>¿Estás seguro que deseas eliminar la referencia entre:</p>
                <ul>
                  <li><strong>SKU:</strong> {getSkuDisplayName(deletingRef?.sku_id)}</li>
                  <li><strong>Material:</strong> {getMaterialName(deletingRef?.material_id)}</li>
                </ul>
                <div className="alert alert-warning">
                  <FaExclamationTriangle className="me-2" />
                  Esta acción eliminará permanentemente la relación y no se puede deshacer.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelDelete}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <FaTrash className="me-2" />
                      Eliminar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SkuMaterialRefsManager;
